/* Atelier - depth layer, behaviour.

   Adds pointer-driven 3D tilt and a tracked highlight to the hero image, to product cards on
   the home page and the collection grid, and on the product page to the gallery, the
   thumbnail rail, the trust tiles and the add-to-cart button.

   Deliberately not a library. One document-level pointermove handles every element on the
   page, which matters on a collection grid that renders 36 cards - 36 listeners and 36 rects
   read per frame is how this pattern usually ends up janking.

   What is adaptive about it: the effect is measured against the device before it is switched
   on, the highlight's strength follows how fast the pointer is actually moving, and work
   stops whenever the tab is hidden. What it is not is machine learning - there is no model
   here, and calling it that would be a lie.

   Gates, in order:
     prefers-reduced-motion   off entirely
     no fine pointer          off (a tilt that needs hover is dead weight on a phone, and the
                              touch target would swallow the tap)
     fewer than 4 cores       off
     deviceMemory under 4GB   off where the browser reports it */
(function () {
  'use strict';

  var MAX_TILT = 7;          // degrees; past about 8 the photograph starts to read as skewed
  var LIFT = 22;             // px the media rises out of the card
  var GLOW_MAX = 0.55;
  var VELOCITY_FULL = 900;   // px/s at which the highlight reaches full strength

  function allowed() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return false;
    if ((navigator.hardwareConcurrency || 8) < 4) return false;
    if (navigator.deviceMemory && navigator.deviceMemory < 4) return false;
    return true;
  }

  /* [selector for the tilting element, selector for the part that lifts, or null].

     Order matters: elementFor takes the first match, so anything that can sit inside another
     target is listed above it.

     The hero tilts .gf-arch rather than its parent .gf-art, because .gf-art carries the
     reveal class and both rules would be writing transform on the same element. The smaller
     product-page targets take the highlight on themselves - there is nothing inside a 60px
     thumbnail worth lifting onto a second plane. Their perspective and lift are retuned in
     atelier-pdp-3d.css. */
  var TARGETS = [
    ['.gf-pdp .thumbnail-list__item', null],
    ['.gf-pdp .gf-pdp-trust__i', null],
    ['.gf-pdp .product-form__submit', null],
    ['.gf-card', '.gf-card__media'],
    ['#product-grid .card-wrapper', '.card__inner'],
    ['.collection .card-wrapper', '.card__inner'],
    ['.gf-hero .gf-arch', null],
    ['.gf-pdp .product__media-wrapper .product__media-item', null]
  ];

  var current = null;
  var lastX = 0, lastY = 0, lastT = 0, velocity = 0;
  var frame = null;
  var pending = null;

  function glint(host) {
    if (host.querySelector(':scope > .t3-glint')) return;
    var span = document.createElement('span');
    span.className = 't3-glint';
    span.setAttribute('aria-hidden', 'true');
    host.appendChild(span);
  }

  function mark() {
    TARGETS.forEach(function (pair) {
      [].forEach.call(document.querySelectorAll(pair[0]), function (el) {
        if (el.hasAttribute('data-t3')) return;
        el.setAttribute('data-t3', '1');
        el.classList.add('t3');

        var lift = pair[1] ? el.querySelector(pair[1]) : null;
        var host = lift || el;

        if (lift) lift.classList.add('t3-lift');
        host.classList.add('t3-sheen');
        glint(host);

        el.style.setProperty('--t3-lift', LIFT + 'px');
      });
    });
  }

  function elementFor(target) {
    if (!target || !target.closest) return null;
    for (var i = 0; i < TARGETS.length; i++) {
      var hit = target.closest(TARGETS[i][0]);
      if (hit) return hit;
    }
    return null;
  }

  function clear(el) {
    if (!el) return;
    el.classList.remove('is-live');
    el.style.setProperty('--t3-rx', '0deg');
    el.style.setProperty('--t3-ry', '0deg');
    el.style.setProperty('--t3-glow', '0');
  }

  function render() {
    frame = null;
    if (!pending) return;

    var el = pending.el, x = pending.x, y = pending.y;
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;

    var px = (x - r.left) / r.width;
    var py = (y - r.top) / r.height;

    /* Tilt away from the pointer on the vertical axis and toward it on the horizontal, which
       is the direction that reads as the element leaning under a finger. */
    el.style.setProperty('--t3-rx', (-(py - 0.5) * 2 * MAX_TILT).toFixed(2) + 'deg');
    el.style.setProperty('--t3-ry', ((px - 0.5) * 2 * MAX_TILT).toFixed(2) + 'deg');
    el.style.setProperty('--t3-mx', (px * 100).toFixed(1) + '%');
    el.style.setProperty('--t3-my', (py * 100).toFixed(1) + '%');

    var strength = Math.min(1, velocity / VELOCITY_FULL);
    /* A floor, so the highlight does not vanish the moment the pointer rests. */
    el.style.setProperty('--t3-glow', (GLOW_MAX * (0.35 + 0.65 * strength)).toFixed(3));
  }

  function move(e) {
    var el = elementFor(e.target);

    if (el !== current) {
      clear(current);
      current = el;
      if (el) el.classList.add('is-live');
    }
    if (!el) return;

    var now = e.timeStamp || performance.now();
    var dt = now - lastT;
    if (dt > 0 && dt < 250) {
      var dx = e.clientX - lastX, dy = e.clientY - lastY;
      var v = Math.sqrt(dx * dx + dy * dy) / (dt / 1000);
      /* Smoothed, or the highlight flickers on every jittery sample. */
      velocity = velocity * 0.7 + v * 0.3;
    }
    lastX = e.clientX; lastY = e.clientY; lastT = now;

    pending = { el: el, x: e.clientX, y: e.clientY };
    if (!frame) frame = window.requestAnimationFrame(render);
  }

  function init() {
    if (!allowed()) return;

    document.documentElement.classList.add('t3-on');
    mark();

    document.addEventListener('pointermove', move, { passive: true });
    document.addEventListener('pointerleave', function () { clear(current); current = null; });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { clear(current); current = null; }
    });

    /* Sections render late on this store - the grid is paginated, several widgets inject
       after load, and the trust row's personalisation tile is cloned in from a template once
       the upload field is known to exist. Marking adds a child, which retriggers this; the
       second pass finds everything already flagged and changes nothing, so it settles. */
    var pendingScan = null;
    new MutationObserver(function () {
      if (pendingScan) return;
      pendingScan = window.setTimeout(function () { pendingScan = null; mark(); }, 250);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();